/**
 * This class is used as a heterogeneous map key that encodes the type of the value to maintain some type-safety.
 * The idea is that a properly implemented map (as in {@link AttributesMapLocalizationContext}) only accepts values
 * whose compile-time type corresponds to the type param of the key.
 * <p>
 * This class also maintains a static registry to make sure that no two key instances have the same underlying string,
 * to avoid defining multiple {@link AttributeKey} instances with the same key and different type parameters.
 *
 * @param <T> the type for values associated with the current key instance.
 * @see core-attribute.ts file for a list of keys supported by this library
 */
// @ts-ignore
export class AttributeKey {
    constructor(_key) {
        this._key = _key;
    }
    /**
     * Creates a new AttributeKey after confirming that no other AttributeKey has been created with the same "key".
     * This may be used to define custom attributes that will be present a
     * {@link LocalizationContext}, and can be retrieved using the getAttribute method.
     * <p>
     * If you define a custom attribute, you are responsible for providing values to your
     * {@link AttributesMapLocalizationContextProvider}. See {@link AttributesProviderBuilder} for a convenient way to
     * do that.
     *
     * @param key the "name" of the new AttributeKey
     * @type <T> the type of values associated with this key
     * @return an AttributeKey with the given key, if none already exists.
     * @throws Error if an attribute with the given key already exists -- AttributeKeys should be
     *                               constants defined exactly once in your code.
     */
    static of(key) {
        if (this.KEY_REGISTRY.has(key)) {
            throw new Error(`Key with name ${key} already exist. AttributeKeys ${JSON.stringify(this.KEY_REGISTRY)} must only be created once.`);
        }
        this.KEY_REGISTRY.add(key);
        return new AttributeKey(key);
    }
    /**
     * @return the key in string
     */
    asString() {
        return this._key;
    }
}
AttributeKey.KEY_REGISTRY = new Set();
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiYXR0cmlidXRlLWtleS5qcyIsInNvdXJjZVJvb3QiOiIiLCJzb3VyY2VzIjpbIi4uLy4uLy4uLy4uLy4uL3NyYy9hdHRyaWJ1dGVzL2F0dHJpYnV0ZS1rZXkudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7Ozs7Ozs7Ozs7R0FVRztBQUNILGFBQWE7QUFDYixNQUFNLE9BQU8sWUFBWTtJQUlyQixZQUEyQixJQUFZO1FBQVosU0FBSSxHQUFKLElBQUksQ0FBUTtJQUFJLENBQUM7SUFFNUM7Ozs7Ozs7Ozs7Ozs7O09BY0c7SUFDSSxNQUFNLENBQUMsRUFBRSxDQUFJLEdBQVc7UUFDM0IsSUFBSSxJQUFJLENBQUMsWUFBWSxDQUFDLEdBQUcsQ0FBQyxHQUFHLENBQUMsRUFBRTtZQUM1QixNQUFNLElBQUksS0FBSyxDQUFDLGlCQUFpQixHQUFHLGlDQUFpQyxJQUFJLENBQUMsU0FBUyxDQUFDLElBQUksQ0FBQyxZQUFZLENBQUMsNkJBQTZCLENBQUMsQ0FBQztTQUN4STtRQUNELElBQUksQ0FBQyxZQUFZLENBQUMsR0FBRyxDQUFDLEdBQUcsQ0FBQyxDQUFDO1FBQzNCLE9BQU8sSUFBSSxZQUFZLENBQUksR0FBRyxDQUFDLENBQUM7SUFDcEMsQ0FBQztJQUVEOztPQUVHO0lBQ0ksUUFBUTtRQUNYLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQztJQUNyQixDQUFDOztBQWhDdUIseUJBQVksR0FBRyxJQUFJLEdBQUcsRUFBRSxDQUFDIn0=